import { Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit } from "@angular/core";
import { DataModeService } from "src/app/modules/menu/data-mode.service";
import { PopupService } from "src/app/modules/popup/popup.service";
import { InitService } from "src/app/shared/services/init.service";
import { LayerService } from "../../layer/layer.service";
import { Router, RouterOutlet } from "@angular/router";
import { SpatialAnalysisDialogService } from "../../spatial-analysis/spatial-analysis-dialog.service";
import { MapContainerService } from "src/app/shared/services/map-container.service";
import { AccumateModule, MODULE_NAMES } from "src/app/shared/consts/icon-features";
import { NotificationService } from "src/app/shared/services/notification.service";
import { BasemapComponent } from "../../basemap/basemap/basemap.component";
import { LegendComponent } from "../../layer/legend/legend.component";
import { ReportSpatialSelectionComponent } from "../../report/report-spatial-selection/report-spatial-selection.component";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIconButton } from "@angular/material/button";
import { NgClass } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { SpatialAnalysisTaskBarComponent } from "../../menu/spatial-analysis-task-bar/spatial-analysis-task-bar.component";
import { BasemapSelectorComponent } from "../../basemap/basemap-selector/basemap-selector.component";
import { TaskInfoComponent } from "../../menu/task-info/task-info.component";
import { PopupComponent } from "../../popup/popup/popup.component";
import { TranslocoPipe } from "@jsverse/transloco";
import { IconFeature } from "src/app/shared/contract/Module/DataMode";
import { LAYER_COMPONENT_CONFIG } from "src/app/shared/consts/component-configs";
import { LayerComponent } from "../../layer/layer/layer.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { PermissionService } from "../../core/services/permission.service";

@Component({
  selector: "app-map-container",
  templateUrl: "./map-container.component.html",
  styleUrl: "./map-container.component.scss",
  imports: [
    BasemapComponent,
    LegendComponent,
    ReportSpatialSelectionComponent,
    MatTooltip,
    MatIconButton,
    NgClass,
    MatIcon,
    SpatialAnalysisTaskBarComponent,
    BasemapSelectorComponent,
    TaskInfoComponent,
    PopupComponent,
    RouterOutlet,
    TranslocoPipe
  ]
})
export class MapContainerComponent implements OnDestroy, OnInit {
  readonly permissionService = inject(PermissionService);
  readonly layerService = inject(LayerService);

  private readonly popupService = inject(PopupService);
  private readonly initService = inject(InitService);
  private readonly dataModeService = inject(DataModeService);
  private readonly spatialAnalysisDialogService = inject(SpatialAnalysisDialogService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mapContainerService = inject(MapContainerService);
  private readonly notificationService = inject(NotificationService);

  constructor() {
    effect(() => {
      const selectedModule = this.dataModeService.selectedDataMode();

      const modulesWithDoubleClickInfo: string[] = [
        MODULE_NAMES.ResidentialRealEstate,
        MODULE_NAMES.CommercialRealEstate,
        MODULE_NAMES.BuildingLand,
        MODULE_NAMES.PortfolioDetails,
        MODULE_NAMES.PortfolioItem
      ];

      if (!selectedModule || modulesWithDoubleClickInfo.includes(selectedModule.module)) {
        this.notificationService.info("Help.Description.ResidentialRealEstate.DoubleClickInfo");
      }
    });
  }

  leftMargin = computed(() => {
    const config = this.mapContainerService.leftComponentConfig();
    if (!config) {
      return 32;
    }

    return config.currentWidth() + 16;
  });
  // Check if the user is allowed to use the satellite image
  BaseMapRight(): boolean {
    return (
      this.initService.InitData.moduleRight.find(e => e.name.includes(MODULE_NAMES.Satellite)) !== null &&
      this.initService.InitData.moduleRight.find(e => e.name.includes(MODULE_NAMES.Satellite)) !== undefined
    );
  }

  ngOnDestroy(): void {
    this.popupService.resetOverlay();
  }

  ngOnInit(): void {
    this.popupService.initPopupOverlay();
    this.layerService.reinitLayers();
  }

  spatialAnalyisModes = this.dataModeService.allowedSpatialAnalysisModes;

  taskIconClicked(feature: IconFeature) {
    type MeasurementMode = "LineMeasurement" | "PolygonMeasurement" | "CircleMeasurement";

    const measurementModules: Record<AccumateModule, MeasurementMode | null> = {
      Rvm: "LineMeasurement",
      LineMeasurement: "LineMeasurement",
      PolygonMeasurement: "PolygonMeasurement",
      CircleMeasurement: "CircleMeasurement",
      BuildingLand: null,
      CommercialRealEstate: null,
      ResidentialRealEstate: null,
      Portfolio: null,
      PortfolioDetails: null,
      PortfolioItem: null,
      Layer: null,
      AddressSearch: null,
      CurrentPosition: null,
      Cache: null,
      Settings: null,
      Satellite: null,
      SpatialAnalysis: null,
      MixedRealEstate: null,
      MixedRealEstateUnitDetails: null
    };

    const mode = measurementModules[feature.module];

    if (!mode) {
      return;
    }

    this.router.navigateByUrl("/").then(() => this.spatialAnalysisDialogService.showDialog(mode));
  }

  openLayers() {
    if (this.layerService.isLayerPanelOpen()) {
      return;
    }

    this.layerService.showLayerPanel();

    const dialogRef = this.dialog.open(LayerComponent, {
      width: LAYER_COMPONENT_CONFIG.collapsedWidth + "px",
      disableClose: true,
      autoFocus: false,
      position: { top: "0", right: "0" },
      maxHeight: "100vh",
      hasBackdrop: false
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dialog.closeAll();
      });
  }

  toggleLegend() {
    this.layerService.showLegend.set(!this.layerService.showLegend());
  }
}
